import { memo } from 'react';
import type { i18n } from 'i18next';
import { useTranslation } from 'react-i18next';

import { UiLanguage } from '@/services/ws-companies/src/main/resources/rest-api';
import type { TranslationDto } from '@/services/ws-translate/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';

import { TranslationModal } from 'src/modules/TranslationModal/index.ts';
import { getCurrentUiLanguage } from 'src/utils/uiLanguage.ts';
import { supportedLanguages } from '../../constants.ts';
import { useTranslateModeContext } from './context.ts';

type TranslationValues = Pick<TranslationDto, 'valueEn' | 'valueDe' | 'valueFr' | 'valueIt' | 'valueDeAt' | 'valueDeDe'>;

type UpdateLocaleCacheParams = {
  i18n: i18n;
  namespace: string;
  key: string;
  values: TranslationValues;
};

const uiLanguageFieldMap: Record<UiLanguage, keyof TranslationValues> = {
  [UiLanguage.DE]: 'valueDe',
  [UiLanguage.DE_AT]: 'valueDeAt',
  [UiLanguage.DE_DE]: 'valueDeDe',
  [UiLanguage.EN]: 'valueEn',
  [UiLanguage.FR]: 'valueFr',
  [UiLanguage.IT]: 'valueIt',
};

/**
 * Patches the LocalStorage cache and i18next resource bundle after a translation is saved,
 * so the updated value is visible immediately without a full page reload.
 *
 * For each supported language, updates the corresponding `i18next_res_{lng}-{ns}` JSON entry
 * in LocalStorage and resets `i18nStamp` to `now` so the cache TTL restarts — otherwise
 * the backend would overwrite the new value with the stale cached response on next load.
 * Also injects the new value into the in-memory i18next store via `addResource`.
 */
const updateLocaleCache = ({ i18n, namespace, key, values }: UpdateLocaleCacheParams) => {
  const now = Date.now();

  supportedLanguages.forEach((lng) => {
    const cacheKey = `i18next_res_${lng}-${namespace}`;
    // the cached bundle of lng holds the texts of the UI language this tab shows
    const uiLanguage = getCurrentUiLanguage(lng);
    const fieldKey = uiLanguage ? uiLanguageFieldMap[uiLanguage] : undefined;
    const newValue = fieldKey ? values[fieldKey] : undefined;

    if (newValue === undefined) {
      return;
    }

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as { i18nStamp?: number; [key: string]: unknown };
        parsed[key] = newValue;
        parsed.i18nStamp = now;
        localStorage.setItem(cacheKey, JSON.stringify(parsed));
      } catch {
        // If parsing fails, skip cache update for this language
      }
    }

    i18n.addResource(lng, namespace, key, newValue);
  });
};

export const TranslationEditor = memo(function TranslationEditor() {
  const { i18n } = useTranslation();
  const editorModalState = useTranslateModeContext('editorModalState');

  const handleTranslationUpdated = useCallbackRef((translation: TranslationDto) => {
    updateLocaleCache({ i18n, namespace: translation.namespace, key: translation.key, values: translation });
    void i18n.changeLanguage(i18n.language);
  });

  if (!editorModalState.isMounted) {
    return null;
  }

  return <TranslationModal state={editorModalState} onUpdated={handleTranslationUpdated} />;
});
